import { Box, Typography } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import useLogout from "../../hooks/use-logout";
import CategorySection from "./category-section";
import { TOOL_CATEGORIES } from "./tools-registry";
import ToolsSidebar, { type SidebarItem } from "./tools-sidebar";

const ALL_TOOLS = "All tools";

const styles = {
  layout: { display: "flex", flexDirection: "row", minHeight: "100vh" },
  content: { flex: 1, p: 3, overflowX: "hidden" },
  subtitle: { mt: 0.5, mb: 1 },
  empty: { mt: 3, color: "text.secondary" },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Internal tools landing page.
 *
 * Thin container: owns the search query and active-category state, derives the
 * filtered tool groups and the sidebar items, and composes `ToolsSidebar` with
 * a content pane of `CategorySection`s. All presentation lives in those child
 * components; all tool data lives in `TOOL_CATEGORIES`.
 */
const Home = () => {
  const navigate = useNavigate();
  const logout = useLogout();
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>(ALL_TOOLS);

  const normalizedQuery = query.trim().toLowerCase();

  const totalCount = useMemo(
    () =>
      TOOL_CATEGORIES.reduce((count, group) => count + group.tools.length, 0),
    [],
  );

  const sidebarItems = useMemo<SidebarItem[]>(
    () => [
      { label: ALL_TOOLS, count: totalCount },
      ...TOOL_CATEGORIES.map((group) => ({
        label: group.category,
        count: group.tools.length,
      })),
    ],
    [totalCount],
  );

  const visibleCategories = useMemo(
    () =>
      TOOL_CATEGORIES.map((group) => ({
        ...group,
        tools: group.tools.filter(
          (tool) =>
            (normalizedQuery === "" ||
              `${tool.name} ${tool.description}`
                .toLowerCase()
                .includes(normalizedQuery)) &&
            (activeCategory === ALL_TOOLS || activeCategory === group.category),
        ),
      })).filter((group) => group.tools.length > 0),
    [normalizedQuery, activeCategory],
  );

  const handleOpenTool = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate],
  );

  return (
    <Box sx={styles.layout}>
      <ToolsSidebar
        activeItem={activeCategory}
        items={sidebarItems}
        query={query}
        onLogout={logout}
        onQueryChange={setQuery}
        onSelectItem={setActiveCategory}
      />

      <Box component="main" sx={styles.content}>
        <Typography variant="h5">Internal tools</Typography>
        <Typography variant="body2" sx={styles.subtitle}>
          Select a tool to get started. Filter by category in the sidebar or
          search by name.
        </Typography>

        {visibleCategories.length === 0 ? (
          <Typography sx={styles.empty}>No tools match “{query}”.</Typography>
        ) : (
          visibleCategories.map((group) => (
            <CategorySection
              key={group.category}
              category={group.category}
              tools={group.tools}
              onOpenTool={handleOpenTool}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default Home;
